import React, { useState } from 'react'
import { Formik, FormikValues } from 'formik'
import { useTranslation } from 'react-i18next'
import { object as yupObject, string as yupString } from 'yup'
import inputs, { IInput } from 'utils/inputs'
import { css, StyleSheet } from 'aphrodite'
import LoadingButton from '@mui/lab/LoadingButton'
import Input from 'components/ui/form/Input'
import Switch from 'components/ui/abstract/Switch'
import { IApplicationState } from 'components/store/types'
import { getFormSubmittingStatus } from 'components/core/state/selectors'
import constants from 'utils/constants'
import ucfirst from 'utils/ucfirst'
import Styles from 'components/ui/Styles'
import { connect, ConnectedProps } from 'react-redux'
import ArrowButton from 'components/ui/buttons/ArrowButton'

const mapState = (state: IApplicationState) => ({
  isSubmitting: getFormSubmittingStatus(state),
})
const connector = connect(mapState)

interface FormProps extends ConnectedProps<typeof connector> {
  onSubmit: (values: FormikValues) => void,
}

const Form = ({ isSubmitting, onSubmit }: FormProps) => {
  const { t } = useTranslation()
  const formInputs = inputs(t)
  const yupSchema = Object.fromEntries(formInputs.map((input: IInput) => [input.name, input.validationSchema]))
  yupSchema.message = yupString()
  yupSchema.newPatient = yupString()
  const validationSchema = yupObject().shape(yupSchema)
  const [needFormValidation, setNeedFormValidation] = useState(false)
  const handleFormSubmit = (handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void) => {
    setNeedFormValidation(true)
    if (!isSubmitting) {
      handleSubmit()
    }
  }
  const initialValues = {
    newPatient: true,
    lastName: '',
    firstName: '',
    phone: '',
    email: '',
    message: '',
  }

  return (
    <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit}>
      {({
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setValues,
        values,
      }) => {
        const [newPatient, setNewPatient] = useState(true)
        return (
          <>
            <div className="tw-col-span-12 md:tw-col-span-6 md:tw-pr-2 tw-flex tw-flex-col">
              <div className="tw-flex tw-justify-between tw-items-center">
                <span className="tw-text-lg">{t('i-am-a-new-patient')}</span>
                <Switch defaultChecked={newPatient} handleSwitch={() => setNewPatient(!newPatient)} name="newPatient" />
              </div>
              {formInputs.map((input: IInput) => {
                const hasError = (needFormValidation && !!errors[input.name])
                  || (!!errors[input.name] && !!touched[input.name])
                return (
                  <Input
                    name={input.name}
                    key={input.name}
                    error={hasError}
                    classes={{
                      root: css(styles.inputs),
                    }}
                    helperText={hasError ? String(errors[input.name]) : undefined}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label={input.placeholder}
                    value={values[input.name]}
                  />
                )
              })}
            </div>
            <div className="tw-col-span-12 md:tw-col-span-6 md:tw-pl-2 tw-mb-4">
              <Input
                name="message"
                label={t('message')}
                variant="outlined"
                classes={{
                  root: css(styles.textArea),
                }}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={10}
                multiline
              />
            </div>
            <div className="tw-col-span-12 tw-mb-4">
              <p className={css(styles.infoText)}>
                {ucfirst(t('step4.infos'))}
              </p>
            </div>
            <div className="tw-col-span-12 tw-flex">
              {isSubmitting
                ? (
                  <LoadingButton
                    loading={isSubmitting}
                    className={css(Styles.button, styles.sendButton)}
                  />
                ) : (
                  <ArrowButton
                    buttonStyle={css(styles.button)}
                    onClick={() => {
                      setValues({ ...values, newPatient })
                      handleFormSubmit(handleSubmit)
                    }}
                    text={t('send')}
                  />
                )}
            </div>
          </>
        )
      }}
    </Formik>
  )
}
const styles = StyleSheet.create({
  button: {
    backgroundColor: constants.colors.green,
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  infoText: {
    fontStyle: 'italic',
    fontSize: '18px',
    lineHeight: '24px',
  },
  inputs: {
    color: constants.colors.white,
    marginBottom: 6,
    borderColor: 'white',
  },
  sendButton: {
    backgroundColor: constants.colors.green,
    minWidth: '175px',
    minHeight: '40px',
  },
  textArea: {
    width: '100%',
    height: '100%',
  },
})
export default connector(Form)
